import { getDownloadURL, ref } from 'firebase/storage';
import { collection, doc, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db, storage } from '@/firebaseInit';
import { coll_invoices, normaliseInvoice, type InvoiceType } from '@/types/types_invoice';

// Data access for invoices. Reads only.
//
// Nothing here writes, and could not: `invoices` is closed to client writes in
// firestore.rules. Drafting one is `generateInvoiceDraft`, issuing one is
// `issueInvoice` and taking one back is `voidInvoice` — all Cloud Functions, see
// src/util/callable_functions.ts. Issuing in particular writes to a second
// collection inside a transaction, which no security rule could express.

const invoicesCollection = collection(db, coll_invoices);

function toInvoice(id: string, data: Record<string, unknown>): InvoiceType {
  return normaliseInvoice(data as Partial<InvoiceType>, id);
}

/**
 * Subscribes to every invoice for one month, whatever state it is in.
 *
 * Keyed on the period rather than listing everything, because the screen is a
 * month at a time: an admin works August, decides August is right, and issues
 * it. Ordered by partner so the list reads the same way twice running.
 */
export function subscribeInvoicesForPeriod(
  period: string,
  onChange: (invoices: InvoiceType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(invoicesCollection, where('period', '==', period), orderBy('partnerName')),
    (snap) => onChange(snap.docs.map((d) => toInvoice(d.id, d.data()))),
    onError,
  );
}

/** Subscribes to one invoice. Live, so issuing it updates the page underneath. */
export function subscribeInvoice(
  invoiceId: string,
  onChange: (invoice: InvoiceType | null) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, coll_invoices, invoiceId),
    (snap) => onChange(snap.exists() ? toInvoice(snap.id, snap.data()) : null),
    onError,
  );
}

/**
 * A URL for the rendered PDF, resolved on demand rather than stored.
 *
 * The invoice keeps a Storage path, not a link — see `InvoiceType.pdfPath`. This
 * exchanges it for a URL using the caller's own credentials, so the storage
 * rules decide, and nothing that outlives somebody's access is ever written down.
 */
export function invoicePdfUrl(pdfPath: string): Promise<string> {
  return getDownloadURL(ref(storage, pdfPath));
}
